import { Component, EventEmitter, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { debounceTime, map, tap } from 'rxjs/operators';
import {
  SaveCustomizations,
  UpdateProperty,
} from '../../actions/customization';
import { Customization } from '../../actions/reducers';
import { BehaviorSubject, Subject } from 'rxjs';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import {
  CUSTOMIZATION_SCHEMA,
  CustomizationEditorSchema,
} from 'ZalamoLibrary/src/lib/ui/customization/types';
import { selectCurrentCustomizationState } from 'ZalamoLibrary/src/lib/ui/customization/actions/selector';
import {
  DirtyForm,
  DirtyFormInterface,
} from 'ZalamoLibrary/src/lib/ui/form/validation/DirtyForm';
import { applyMixins } from 'ZalamoLibrary/src/lib/core/classes';
import { FullscreenService } from 'ZalamoLibrary/src/lib/ui/fullscreen/fullscreen.service';

@Component({
    selector: 'lib-customization-editor',
    templateUrl: './customization-editor.component.html',
    styleUrls: ['./customization-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomizationEditorComponent
  extends AliveState
  implements OnInit, DirtyFormInterface
{
  types = {
    'zalamo-main-background': 'color',
  };
  elements$ = this.store.pipe(
    select(selectCurrentCustomizationState),
    map((all) => {
      const res: Array<{
        name: string;
        value: string;
        type: 'color' | 'unit';
      }> = [];
      for (const type in all) {
        if (all.hasOwnProperty(type)) {
          res.push({
            type: this.types[type],
            value: all[type],
            name: type,
          });
        }
      }
      return res;
    })
  );
  updateProperty$ = new Subject<{ value; name }>();
  currentVars$ = new BehaviorSubject<
    Array<{ type: string; value: string; name: string }>
  >([]);
  changedProperties: Record<string, string> = {};
  originalProperties: Record<string, string> = {};
  varMap: Record<string, string> = {};
  closeMe = new EventEmitter();

  constructor(
    private store: Store<{ customization: { customization: Customization } }>,
    @Inject(CUSTOMIZATION_SCHEMA)
    public customizationSchema: CustomizationEditorSchema,
    public fullscreenService: FullscreenService
  ) {
    super();
  }

  updateProperty(name, value) {
    console.log(name, value);
    this.updateProperty$.next({ value, name });
  }

  takeSnapshotForDirty(): string {
    return JSON.stringify({
      ...this.originalProperties,
      ...this.changedProperties,
    });
  }

  ngOnInit(): void {
    const usedVars = {};
    const regexp = /var\(--zalamo-editable-([a-zA-Z0-9\-_]+)\)/gm;
    document
      .getElementsByTagName('head')[0]
      .querySelectorAll('style')
      .forEach((e) => {
        const res = /var\(--zalamo-editable-([a-zA-Z0-9\-_]+)\)/gm.exec(
          e.innerText
        );
        console.log(res);
        let array1;

        while ((array1 = regexp.exec(e.innerText)) !== null) {
          console.log(`Found ${array1[1]}`);
          // expected output: "Found foo. Next starts at 9."
          // expected output: "Found foo. Next starts at 19."
          if (!usedVars[array1[1]]) {
            usedVars[array1[1]] = 0;
          }
          usedVars[array1[1]]++;
        }
      });
    const preSort = [];
    for (const x in usedVars) {
      preSort.push({
        var: x,
        priority: usedVars[x],
        value: getComputedStyle(document.body).getPropertyValue(
          `--zalamo-editable-${x}`
        ),
      });
      this.varMap[x] = getComputedStyle(document.body).getPropertyValue(
        `--zalamo-editable-${x}`
      );
    }
    this.originalProperties = { ...this.varMap };
    this.currentFormValue = this.takeSnapshotForDirty();

    this.currentVars$.next(
      preSort
        .sort((a, b) => a.priority - b.priority)
        .map((value) => ({
          value: value.value,
          name: `zalamo-editable-${value.var}`,
          type: this.types[`--zalamo-editable-${value.name}`],
        }))
    );
    this.subscribeWhileAlive(
      this.updateProperty$.pipe(
        tap((x) => console.log(x)),
        debounceTime(300),
        tap(({ value, name }) => {
          this.changedProperties[name.substring(16)] = value;
          this.store.dispatch(UpdateProperty({ value, name }));
        })
      )
    );
  }

  cancel() {
    Object.keys(this.changedProperties).forEach((name) => {
      this.store.dispatch(
        UpdateProperty({
          value: this.originalProperties[name],
          name: `zalamo-editable-${name}`,
        })
      );
    });
    this.closeMe.next(true);
  }

  save() {
    this.store.dispatch(
      SaveCustomizations({
        customization: Object.entries(this.changedProperties).map(
          ([name, value]) => ({ name: `zalamo-editable-${name}`, value })
        ),
      })
    );
    this.closeMe.next(true);
  }

  track(x) {
    return x;
  }
}

export interface CustomizationEditorComponent extends DirtyFormInterface {}

applyMixins(CustomizationEditorComponent, [DirtyForm]);
